/**
 * Region-export pipeline — the three single-image Region modes
 * (Selection / Active Layer / Visible Layers) in the ExportDialog.
 *
 * Sibling to `slice-export.ts`: the slice path produces N files
 * (one per named rect, bundled in a ZIP); this path produces ONE
 * file per export, just with different bounds-resolution logic.
 *
 * Each builder takes already-rasterized inputs (full-canvas
 * snapshot + selection mask canvas + per-layer composite payload)
 * and returns a fresh canvas sized to the region's natural pixel
 * bbox. Filename construction lives in the dialog now — the input
 * shows the user's typed name as the live source of truth, with
 * a region-specific default seeded when the user picks a region.
 *
 * Why pure functions:
 *  - The Fabric canvas hook is too heavy to mock in tests; pulling
 *    the math out keeps it unit-testable.
 *  - Each builder produces a canvas the existing format-dispatch
 *    path (PNG/JPEG/WebP/AVIF via `canvas.toBlob`; PDF via
 *    `encodeCanvasAsPdfBlob`; TIFF via the utif lazy-import) can
 *    consume unchanged. No new encoder code lives here.
 */

/** Build the Selection-region canvas: crop the full-canvas raster
 *  to the selection's tight bbox, then clip pixels OUTSIDE the
 *  selection mask to transparent via a `destination-in` composite.
 *
 *  The mask canvas is at FULL canvas dimensions with alpha
 *  encoding the binary selection (returned by the canvas hook's
 *  `getSelectionAsMaskCanvas`); the bbox is what
 *  `getMarqueeBounds` returns. We translate the mask draw by the
 *  bbox origin so the destination-in compares apples to apples.
 *
 *  Returns null when the bbox is degenerate (zero / negative
 *  dimensions, or the mask canvas dims don't match the
 *  full-canvas dims — both are programming-error cases the caller
 *  should treat as "skip"). */
export function buildSelectionRegionCanvas(
  fullCanvas: HTMLCanvasElement,
  maskCanvas: HTMLCanvasElement,
  bounds: { left: number; top: number; width: number; height: number },
): HTMLCanvasElement | null {
  const w = Math.round(bounds.width);
  const h = Math.round(bounds.height);
  if (w <= 0 || h <= 0) return null;
  // Mask must be at the same dimensions as the full canvas — that's
  // the contract `getSelectionAsMaskCanvas` ships. A mismatch means
  // the caller fed mismatched snapshots from different timeline
  // points; fail closed rather than producing garbage output.
  if (maskCanvas.width !== fullCanvas.width || maskCanvas.height !== fullCanvas.height) {
    return null;
  }
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d');
  if (!ctx) return null;
  const sx = Math.max(0, Math.round(bounds.left));
  const sy = Math.max(0, Math.round(bounds.top));
  // Clamp to canvas bounds — if the marquee bbox runs off the right
  // / bottom edge (which shouldn't normally happen, but
  // `getMarqueeBounds` reflects whatever the user drew), we draw
  // only the in-canvas portion. The drawImage source rect would
  // otherwise be partially OOB which most browsers handle by
  // clamping but a few historically threw.
  const sw = Math.min(w, fullCanvas.width - sx);
  const sh = Math.min(h, fullCanvas.height - sy);
  if (sw <= 0 || sh <= 0) return null;
  ctx.drawImage(fullCanvas, sx, sy, sw, sh, 0, 0, sw, sh);
  // Alpha cutout for non-rectangular selections (lasso / wand /
  // ellipse). `destination-in` keeps existing pixels only where the
  // mask has non-zero alpha; the mask is at full-canvas coords so
  // we draw it offset by the bbox origin to line up.
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(maskCanvas, -sx, -sy);
  ctx.globalCompositeOperation = 'source-over';
  return out;
}

/** Build the Active-Layer-region canvas from the layer's
 *  prerendered payload (`useFabricCanvas.getLayerExportPayload`).
 *  The payload's `canvas` is already sized to the layer's content
 *  bbox in scene-space pixels — wrap it in a copy so the caller can
 *  safely encode/dispose without mutating the live paint surface. */
export function buildActiveLayerRegionCanvas(payload: {
  canvas: HTMLCanvasElement;
}): HTMLCanvasElement | null {
  const w = payload.canvas.width;
  const h = payload.canvas.height;
  if (w <= 0 || h <= 0) return null;
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(payload.canvas, 0, 0);
  return out;
}

/** Build the Visible-Layers-region canvas. Fabric's renderer
 *  already skips `visible: false` objects, so the full-canvas
 *  snapshot the caller passes in IS the visible-layers composite —
 *  no filtering work needed at this layer. We still copy the
 *  source into a fresh canvas so the caller can encode without
 *  mutating the snapshot, and to keep the API symmetric with the
 *  other two builders.
 *
 *  The Region option's user-facing affordance is "make it explicit
 *  in the export that I want what I see right now, not whatever
 *  hidden layers might be lurking" — even though Whole Canvas
 *  produces the same bytes today. Documenting the equivalence in
 *  code rather than collapsing the modes preserves the option for
 *  a future Whole-Canvas-includes-hidden behavior switch. */
export function buildVisibleLayersRegionCanvas(
  fullCanvas: HTMLCanvasElement,
): HTMLCanvasElement | null {
  const w = fullCanvas.width;
  const h = fullCanvas.height;
  if (w <= 0 || h <= 0) return null;
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(fullCanvas, 0, 0);
  return out;
}

/** Apply the Scale multiplier (1x/2x/3x) to a region's natural
 *  canvas. Mirrors the slice path's `cropCanvasToSlice` choice of
 *  `imageSmoothingQuality: 'high'` so 2×/3× upscales look
 *  reasonable. Returns the original canvas untouched when scale is
 *  1 — saves the alloc + draw round-trip for the common case. */
export function scaleRegionCanvas(
  canvas: HTMLCanvasElement,
  scaleMultiplier: number,
): HTMLCanvasElement {
  if (scaleMultiplier === 1) return canvas;
  const outW = Math.max(1, Math.round(canvas.width * scaleMultiplier));
  const outH = Math.max(1, Math.round(canvas.height * scaleMultiplier));
  const out = document.createElement('canvas');
  out.width = outW;
  out.height = outH;
  const ctx = out.getContext('2d');
  if (!ctx) return canvas;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(canvas, 0, 0, canvas.width, canvas.height, 0, 0, outW, outH);
  return out;
}
